import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';

/**
 * Simplified Auth component for older browsers
 * Uses minimal styling and simpler DOM structure for better compatibility
 */
export const AuthCompat = () => {
  const [mode, setMode] = useState<'signin' | 'signup' | 'reset' | 'confirmation'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [resetCooldown, setResetCooldown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // If user is already logged in (e.g. OAuth redirect landed here), go to home
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) navigate('/', { replace: true });
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        const userId = session.user.id;
        const isOAuth = session.user.app_metadata?.provider !== 'email';
        if (isOAuth) {
          try {
            const utmData: Record<string, string | null> = {};
            for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const) {
              utmData[key] = localStorage.getItem(`atlas:${key}`) || null;
            }
            utmData.fbc = localStorage.getItem('atlas:fbc') || null;
            utmData.fbp = localStorage.getItem('atlas:fbp') || null;
            let refCode: string | null = null;
            try {
              const params = new URLSearchParams(window.location.search);
              refCode = params.get('ref');
              if (!refCode) refCode = localStorage.getItem('atlas:ref');
            } catch {}
            if (refCode) utmData.ref_code = refCode;
            const hasAny = Object.values(utmData).some((v) => v !== null);
            if (hasAny) await supabase.from('profiles').update(utmData).eq('id', userId);
          } catch {}
          try {
            await fetch('https://ai-agency-dt7d.onrender.com/webhook/resgistro_user', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: session.user.email }),
            });
          } catch {}
        }
        navigate('/', { replace: true });
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  // Initialize mode from URL query param (e.g., /auth?mode=signup)
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const m = params.get('mode');
      if (m === 'signup' || m === 'signin' || m === 'reset') {
        setMode(m as 'signin' | 'signup' | 'reset');
      }
    } catch (e) {
      // ignore parsing errors
    }
  }, [location.search]);

  // Capture UTM params, ref code, and Meta cookies on page load
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
      for (const key of utmKeys) {
        const val = params.get(key);
        if (val) {
          try { localStorage.setItem(`atlas:${key}`, val); } catch {}
        }
      }
      const ref = params.get('ref');
      if (ref) {
        try { localStorage.setItem('atlas:ref', ref); } catch {}
      }
      const getCookie = (name: string): string | null => {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
      };
      const fbc = getCookie('_fbc');
      const fbp = getCookie('_fbp');
      if (fbc) { try { localStorage.setItem('atlas:fbc', fbc); } catch {} }
      if (fbp) { try { localStorage.setItem('atlas:fbp', fbp); } catch {} }
    } catch {}
  }, []);

  useEffect(() => {
    if (!resendCooldown) return;
    const id = setInterval(() => {
      setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  // Cooldown timer for reset password to prevent multiple emails
  useEffect(() => {
    if (!resetCooldown) return;
    const id = setInterval(() => {
      setResetCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [resetCooldown]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);

    try {
      if (mode === 'signin') {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        
        if (error) {
          if (error.message?.includes('Email not confirmed')) {
            throw new Error('Email no confirmado. Por favor, revisa tu bandeja de entrada y haz clic en el enlace de confirmación para activar tu cuenta.');
          }
          throw error;
        }

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('No user found');

        // Delegate onboarding decisions to ProtectedRoute in App.tsx
        navigate('/');
      } else if (mode === 'signup') {
        // Capture referral code from URL or localStorage and send as metadata
        let refCode: string | null = null;
        try {
          const params = new URLSearchParams(window.location.search);
          refCode = params.get('ref');
          if (!refCode) {
            try {
              refCode = localStorage.getItem('atlas:ref');
            } catch {}
          } else {
            try {
              localStorage.setItem('atlas:ref', refCode);
            } catch {}
          }
        } catch {}

        const signUpPayload: { email: string; password: string; options?: { data: { ref_code: string } } } = {
          email,
          password,
        };
        if (refCode) {
          signUpPayload.options = { data: { ref_code: refCode } };
        }

        const { error } = await supabase.auth.signUp(signUpPayload);
        if (error) throw error;
        
        // Send registration webhook
        try {
          await fetch('https://ai-agency-dt7d.onrender.com/webhook/resgistro_user', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
        } catch (webhookError) {
          console.error('Error sending registration webhook:', webhookError);
        }
        
        setMode('confirmation');
      } else if (mode === 'reset') {
        // Block if already in cooldown to prevent multiple emails
        if (resetCooldown > 0) {
          setError(`Por favor esperá ${resetCooldown} segundos antes de solicitar otro email.`);
          setLoading(false);
          return;
        }
        
        // Send reset email and wait for response
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/restablecer-clave`,
        });
        
        if (resetError) {
          console.error('Error sending reset email:', resetError);
        }
        
        // Set cooldown to prevent multiple requests (60 seconds)
        setResetCooldown(60);
        setMessage('Si existe una cuenta con este email, recibirás instrucciones para restablecer tu contraseña.');
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'Invalid login credentials') {
          setError('Correo electrónico o contraseña incorrectos');
        } else {
          setError(error.message);
        }
      } else {
        setError('Ocurrió un error inesperado');
      }
    } finally {
      setLoading(false);
    }
  };

  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogleAuth = async () => {
    setError(null);
    setGoogleLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth`,
        },
      });
      if (error) throw error;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al iniciar sesión con Google');
      setGoogleLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!email || resendCooldown > 0 || resendLoading) return;
    setError(null);
    setMessage(null);
    setResendLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });
      if (error) throw error;
      setMessage('Te enviamos un nuevo correo de confirmación.');
      setResendCooldown(60);
    } catch (err: any) {
      setError(err?.message || 'No se pudo reenviar el correo de confirmación');
    } finally {
      setResendLoading(false);
    }
  };

  // Basic inline styles to avoid any CSS compatibility issues
  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#EFF0F5',
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    } as React.CSSProperties,
    card: {
      maxWidth: '400px',
      width: '100%',
      backgroundColor: '#FFFFFF',
      borderRadius: '16px',
      padding: '24px',
      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
    },
    logoContainer: {
      textAlign: 'center' as const,
      marginBottom: '24px',
    },
    logo: {
      width: '80px',
      height: '80px',
    },
    title: {
      fontSize: '24px',
      fontWeight: 600,
      color: '#1F2937',
      marginBottom: '8px',
    },
    subtitle: {
      fontSize: '16px',
      color: '#6B7280',
      marginBottom: '24px',
    },
    error: {
      backgroundColor: '#FEF2F2',
      borderColor: '#FECACA',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderRadius: '4px',
      color: '#DC2626',
      padding: '12px',
      fontSize: '14px',
      marginBottom: '16px',
    },
    success: {
      backgroundColor: '#F0FDF4',
      borderColor: '#DCFCE7',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderRadius: '4px',
      color: '#16A34A',
      padding: '12px',
      fontSize: '14px',
      marginBottom: '16px',
    },
    formGroup: {
      marginBottom: '16px',
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '4px',
    },
    input: {
      width: '100%',
      height: '44px',
      padding: '0 16px 0 16px',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: '#D1D5DB',
      borderRadius: '9999px',
      fontSize: '16px',
      backgroundColor: '#FFFFFF',
    },
    button: {
      width: '100%',
      backgroundColor: '#3B82F6', // blue-500
      color: '#FFFFFF',
      height: '44px',
      padding: '0 16px',
      borderRadius: '9999px', 
      border: 'none',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 400, // regular, not medium or bold as per memory
      marginTop: '8px',
      marginBottom: '8px',
    },
    disabledButton: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    linkButton: {
      backgroundColor: 'transparent',
      color: '#3B82F6', // blue-500
      border: 'none',
      cursor: 'pointer',
      padding: '4px 8px',
      fontSize: '14px',
    },
    footer: {
      textAlign: 'center' as const,
      marginTop: '24px',
      fontSize: '14px',
      color: '#4B5563',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoContainer}>
          <img 
            src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system//Logo.svg" 
            alt="Atlas logo" 
            style={styles.logo} 
          />
          <div style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', marginTop: '8px' }}>
            <span style={{ fontSize: '28px', fontWeight: 600, color: '#3B82F6' }}>Atlas</span>
          </div>
        </div>

        {/* Auth Content */}
        <div>
          <div style={styles.title}>
            {mode === 'signin' && 'Iniciar Sesión'}
            {mode === 'signup' && 'Crear Cuenta'}
            {mode === 'reset' && 'Restablecer Contraseña'}
            {mode === 'confirmation' && 'Confirma tu Email'}
          </div>
          <div style={styles.subtitle}>
            {mode === 'signin' && 'Bienvenido de vuelta a Atlas'}
            {mode === 'signup' && 'Únete a Atlas para empezar'}
            {mode === 'reset' && 'Ingresa tu email para restablecer tu contraseña'}
            {mode === 'confirmation' && 'Hemos enviado un correo de confirmación a tu email'}
          </div>

          {/* Error Message */}
          {error && <div style={styles.error}>{error}</div>}

          {/* Success Message */}
          {message && <div style={styles.success}>{message}</div>}

          {mode === 'confirmation' ? (
            <div>
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <p style={{ color: '#4B5563', marginBottom: '8px' }}>
                  Hemos enviado un correo de confirmación a <strong>{email}</strong>
                </p>
                <p style={{ color: '#6B7280', fontSize: '14px' }}>
                  Por favor, revisa tu bandeja de entrada y haz clic en el enlace de confirmación para activar tu cuenta.
                </p>
              </div>
              
              <button
                onClick={() => setMode('signin')}
                style={styles.button}
              >
                Ya lo confirmé
              </button>
              <button
                onClick={handleResendConfirmation}
                disabled={resendCooldown > 0 || resendLoading}
                style={resendCooldown > 0 || resendLoading ? { ...styles.linkButton, ...styles.disabledButton } : styles.linkButton}
              >
                {resendCooldown > 0 ? (
                  <span>
                    Reenviar mail
                    <br />
                    {`(Podés reintentar en ${resendCooldown} seg)`}
                  </span>
                ) : (
                  'Reenviar mail'
                )}
              </button>
            </div>
          ) : (
            <>
              {/* Google OAuth button — shown on signin and signup */}
              {(mode === 'signin' || mode === 'signup') && (
                <div style={{ marginBottom: '16px' }}>
                  <button
                    type="button"
                    onClick={handleGoogleAuth}
                    disabled={googleLoading}
                    style={{
                      width: '100%',
                      height: '44px',
                      padding: '0 16px',
                      borderRadius: '9999px',
                      border: '1px solid #D1D5DB',
                      backgroundColor: '#FFFFFF',
                      cursor: googleLoading ? 'not-allowed' : 'pointer',
                      opacity: googleLoading ? 0.5 : 1,
                      fontSize: '16px',
                      fontWeight: 500,
                      color: '#1F2937',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                    }}
                  >
                    {googleLoading ? 'Cargando...' : (
                      <>
                        <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4"/>
                          <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853"/>
                          <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z" fill="#FBBC05"/>
                          <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58Z" fill="#EA4335"/>
                        </svg>
                        {mode === 'signin' ? 'Iniciar sesión con Google' : 'Registrarse con Google'}
                      </>
                    )}
                  </button>
                  <div style={{ display: 'flex', alignItems: 'center', margin: '16px 0' }}>
                    <div style={{ flex: 1, height: '1px', backgroundColor: '#D1D5DB' }} />
                    <span style={{ padding: '0 12px', fontSize: '14px', color: '#9CA3AF' }}>o con email</span>
                    <div style={{ flex: 1, height: '1px', backgroundColor: '#D1D5DB' }} />
                  </div>
                </div>
              )}
            <form onSubmit={handleAuth}>
              <div style={styles.formGroup}>
                <label htmlFor="email" style={styles.label}>
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={styles.input}
                  placeholder="tu@email.com"
                  required
                />
              </div>

              {mode !== 'reset' && (
                <div style={styles.formGroup}>
                  <label htmlFor="password" style={styles.label}>
                    Contraseña
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={styles.input}
                    placeholder="••••••••"
                    required
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={loading ? { ...styles.button, ...styles.disabledButton } : styles.button}
              >
                {loading ? (
                  mode === 'reset' ? 'Enviando mail...' : 'Cargando...'
                ) : (
                  mode === 'signin' ? 'Iniciar Sesión' :
                  mode === 'signup' ? 'Crear Cuenta' :
                  'Enviar Instrucciones'
                )}
              </button>
            </form>
            </>
          )}

          <div style={styles.footer}>
            {mode === 'signin' && (
              <div>
                <div>
                  <button
                    onClick={() => setMode('reset')}
                    style={styles.linkButton}
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
                <div>
                  ¿No tienes una cuenta?{' '}
                  <button
                    onClick={() => setMode('signup')}
                    style={styles.linkButton}
                  >
                    Regístrate
                  </button>
                </div>
              </div>
            )}
            {mode === 'signup' && (
              <div>
                ¿Ya tienes una cuenta?{' '}
                <button
                  onClick={() => setMode('signin')}
                  style={styles.linkButton}
                >
                  Inicia Sesión
                </button>
              </div>
            )}
            {mode === 'reset' && (
              <button
                onClick={() => setMode('signin')}
                style={styles.linkButton}
              >
                Volver a Iniciar Sesión
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
